import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react"

import { useAgentsById } from "@/agents/catalog"
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@coval/ui"
import { cn } from "@coval/ui/cn"
import { ChevronDown, ChevronUp } from "lucide-react"

import { seriesRamp } from "@/components/charts/seriesPalette"
import { MotionPauseButton } from "@/components/MotionPauseButton"
import { usePausableMotion } from "@/hooks/usePausableMotion"

import { useUploadedConversationsVisualizerClickForwarding } from "../hooks/useUploadedConversationsVisualizerClickForwarding"
import { useLatestConversationClusteringRunTransport } from "../transport/hooks/useLatestConversationClusteringRunTransport"
import { UploadedConversationsCallTopicTree } from "./UploadedConversationsCallTopicTree"
import { UploadedConversationsVisualizerAnomaliesQuestions } from "./UploadedConversationsVisualizerAnomaliesQuestions"
import { UploadedConversationsVisualizerControlsColumn } from "./UploadedConversationsVisualizerControlsColumn"
import { UploadedConversationsVisualizerConversationOverlay } from "./UploadedConversationsVisualizerConversationOverlay"
import {
  buildNoLensVisualizerLens,
  buildVisualizerAgentsFromClusteringRun,
  NO_LENS_VISUALIZER_LENS_KEY,
  resolveVisualizerAgent,
  resolveVisualizerLens,
  SAMPLE_VISUALIZER_AGENTS,
} from "./uploadedConversationsVisualizerDataset"
import { describeVisualizerLens } from "./uploadedConversationsVisualizerDescription"
import { UploadedConversationsVisualizerFailureGroups } from "./UploadedConversationsVisualizerFailureGroups"
import { UploadedConversationsVisualizerLegendReel } from "./UploadedConversationsVisualizerLegendReel"
import {
  easeInOutSine,
  easeOutCubic,
  LENS_RELEASE_PHASE_SHARE,
  LENS_TRANSITION_DURATION_MS,
} from "./uploadedConversationsVisualizerMotion"
import { createVisualizerMotionClock } from "./uploadedConversationsVisualizerMotionClock"
import {
  buildBackgroundParticles,
  clampNumber,
  clampToPlate,
  computeBackgroundChaosPoint,
  computeBackgroundScatterPoint,
  computeIdleAmbientDriftPoint,
  computeIdleAmbientParticlePoint,
  IDLE_TRANSITION_REGROUP_LANDING_MIX,
  PLATE_DOMAIN_RADIUS,
  projectToScreen,
  resolveCanvasColorPalette,
  type BackgroundParticle,
} from "./uploadedConversationsVisualizerParticles"
import { buildSetRegionSlices } from "./UploadedConversationsVisualizerPieBackground"
import { UploadedConversationsVisualizerRunStatus } from "./UploadedConversationsVisualizerRunStatus"

const PLATE_MASK_CLASS =
  "[mask-image:radial-gradient(circle,_rgba(0,0,0,1)_0%,_rgba(0,0,0,1)_72%,_rgba(0,0,0,0.95)_80%,_rgba(0,0,0,0.78)_87%,_rgba(0,0,0,0.45)_93%,_rgba(0,0,0,0.16)_97%,_transparent_100%)] [-webkit-mask-image:radial-gradient(circle,_rgba(0,0,0,1)_0%,_rgba(0,0,0,1)_72%,_rgba(0,0,0,0.95)_80%,_rgba(0,0,0,0.78)_87%,_rgba(0,0,0,0.45)_93%,_rgba(0,0,0,0.16)_97%,_transparent_100%)]"
const UNDERLAY_MASK_CLASS =
  "[mask-image:radial-gradient(circle,_rgba(0,0,0,1)_0%,_rgba(0,0,0,1)_82%,_rgba(0,0,0,0.94)_90%,_rgba(0,0,0,0.72)_96%,_transparent_100%)] [-webkit-mask-image:radial-gradient(circle,_rgba(0,0,0,1)_0%,_rgba(0,0,0,1)_82%,_rgba(0,0,0,0.94)_90%,_rgba(0,0,0,0.72)_96%,_transparent_100%)]"
const AMBIENT_DOT_COLOR = "var(--coval-text-muted)"
const AMBIENT_DOT_RADIUS_SCALE = 0.7
const AMBIENT_DOT_ALPHA_SCALE = 1.3
const IDLE_AMBIENT_NODAL_STRENGTH = 0.35
const IDLE_BACKGROUND_SLICES = [{ startAngle: -Math.PI / 2, span: Math.PI * 2 }]
const IDLE_BACKGROUND_PRESET = { m: 3, n: 4 }

interface UploadedConversationsVisualizerPanelProps {
  onClusterFilterRequest?: (simulationOutputIds: string[]) => void
  onConversationClick?: (conversationId: string) => void
}

export function UploadedConversationsVisualizerPanel({
  onClusterFilterRequest,
  onConversationClick,
}: UploadedConversationsVisualizerPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const stageSummaryId = useId()
  const [selectedAgentKey, setSelectedAgentKey] = useState<string | null>(null)
  const [selectedLensKey, setSelectedLensKey] = useState<string | null>(null)
  const [activeAnomalyConversationIds, setActiveAnomalyConversationIds] =
    useState<ReadonlySet<string> | null>(null)
  const [activeAnomalyMarkerColor, setActiveAnomalyMarkerColor] = useState<
    string | null
  >(null)
  const [hoveredLegendBucketKey, setHoveredLegendBucketKey] = useState<
    string | null
  >(null)
  const plateRef = useRef<HTMLDivElement>(null)
  const underlayCanvasRef = useRef<HTMLCanvasElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // The plate drifts for as long as the page is open, so it offers a pause
  // control and starts still under a reduced-motion preference.
  const { isStill: isMotionPaused, toggleStill } = usePausableMotion()
  const isStill = isMotionPaused || !isExpanded
  const isStillRef = useRef(isStill)
  const syncMotionRef = useRef<(() => void) | null>(null)
  const [motionClock] = useState(() =>
    createVisualizerMotionClock(() => isStillRef.current),
  )
  const latestRunQuery = useLatestConversationClusteringRunTransport()
  const { agentsById } = useAgentsById()
  const latestRunAgents = useMemo(
    () =>
      latestRunQuery.data
        ? buildVisualizerAgentsFromClusteringRun(
            latestRunQuery.data,
            agentsById,
          )
        : [],
    [latestRunQuery.data, agentsById],
  )
  const isShowingLatestRun = latestRunAgents.length > 0
  const hasCompletedEmptyRun =
    latestRunQuery.data?.schema_version === 2 && latestRunAgents.length === 0
  const showFallback =
    !latestRunQuery.isPending && !isShowingLatestRun && !hasCompletedEmptyRun
  const isLatestRunReadFailure =
    latestRunQuery.isError &&
    ![404, 409].includes(latestRunQuery.error?.status ?? 0)
  const agents = isShowingLatestRun ? latestRunAgents : SAMPLE_VISUALIZER_AGENTS
  const selectedAgent = useMemo(
    () => resolveVisualizerAgent(agents, selectedAgentKey),
    [agents, selectedAgentKey],
  )
  const noLens = useMemo(
    () => buildNoLensVisualizerLens(selectedAgent),
    [selectedAgent],
  )
  const previousParticlesRef = useRef<BackgroundParticle[] | null>(null)
  const lensTransitionGenerationRef = useRef(0)
  const selectedLens = useMemo(
    () =>
      selectedLensKey
        ? resolveVisualizerLens(selectedAgent, selectedLensKey)
        : noLens,
    [noLens, selectedAgent, selectedLensKey],
  )
  const plottedConversationCount = selectedLens.bucketAssignments.reduce(
    (total, bucketAssignment) => total + bucketAssignment.conversations.length,
    0,
  )
  const displayedConversationCount = hasCompletedEmptyRun
    ? 0
    : (selectedLens.failureOutput?.conversation_count ??
      plottedConversationCount)
  const selectedVisualizationKey = `${selectedAgent.key}:${selectedLens.key}`
  const previousVisualizationKeyRef = useRef(selectedVisualizationKey)
  const activeSlices = useMemo(
    () => buildSetRegionSlices(selectedLens.buckets),
    [selectedLens],
  )
  const isAnomaliesLensSelected = selectedLens.key === "anomalies"
  const isFailuresLensSelected = selectedLens.key === "failures"
  const isNoLensSelected = selectedLens.key === NO_LENS_VISUALIZER_LENS_KEY
  const isSeedClusterLensSelected =
    selectedLens.key === "call-topic" || selectedLens.key === "personas"
  const paletteIndexBySlice = useMemo(() => {
    const parentIndexByChildId = new Map(
      selectedLens.failureOutput?.parents.flatMap((parent, parentIndex) =>
        (parent.child_cluster_ids ?? []).map(
          (childId) => [childId, parentIndex] as const,
        ),
      ) ?? [],
    )
    return activeSlices.map(
      (slice, index) => parentIndexByChildId.get(slice.bucket.key) ?? index,
    )
  }, [activeSlices, selectedLens.failureOutput])
  const sliceFillPalette = useMemo(
    () => paletteIndexBySlice.map((index) => seriesRamp(index).light),
    [paletteIndexBySlice],
  )
  const sliceMarkPalette = useMemo(
    () => paletteIndexBySlice.map((index) => seriesRamp(index).mid),
    [paletteIndexBySlice],
  )
  const activeLegendBucketKey =
    isAnomaliesLensSelected || isFailuresLensSelected
      ? null
      : hoveredLegendBucketKey

  const handleSliceHoverChange = useCallback((bucketKey: string | null) => {
    setHoveredLegendBucketKey(bucketKey)
  }, [])

  const handleLegendBucketHoverChange = useCallback(
    (bucketKey: string | null) => {
      setHoveredLegendBucketKey(bucketKey)
    },
    [],
  )

  const {
    handleConversationClick,
    handleConversationIdsFilter,
    handleLegendBucketClick,
  } = useUploadedConversationsVisualizerClickForwarding({
    bucketAssignments: selectedLens.bucketAssignments,
    isShowingLatestRun,
    onClusterFilterRequest,
    onConversationClick,
  })

  const hasMultipleAgents = agents.length > 1

  const selectAgent = (agentKey: string) => {
    if (agentKey === selectedAgent.key) {
      return
    }
    setSelectedAgentKey(agentKey)
  }

  const selectLens = (lensKey: string) => {
    setSelectedLensKey((currentLensKey) =>
      currentLensKey === lensKey ? null : lensKey,
    )
  }

  useEffect(() => {
    const activeBucketKeys = new Set(
      activeSlices.map((slice) => slice.bucket.key),
    )
    setHoveredLegendBucketKey((currentBucketKey) => {
      if (currentBucketKey && activeBucketKeys.has(currentBucketKey)) {
        return currentBucketKey
      }
      return null
    })
  }, [activeSlices])

  useEffect(() => {
    const plate = plateRef.current
    const canvas = canvasRef.current
    if (!plate || !canvas) {
      return
    }

    const context = canvas.getContext("2d")
    if (!context) {
      return
    }

    const shouldAnimateLensTransition =
      // A still plate jumps to the new layout, since no frames follow to animate it.
      !isStillRef.current &&
      previousVisualizationKeyRef.current !== selectedVisualizationKey &&
      Boolean(previousParticlesRef.current?.length)
    if (shouldAnimateLensTransition) {
      lensTransitionGenerationRef.current += 1
    }
    const transitionSeed = lensTransitionGenerationRef.current

    let width = 0
    let height = 0
    let dpr = 1
    let animationFrame = 0
    let particles: BackgroundParticle[] = []
    const useNoisyAmbientBackdrop =
      isSeedClusterLensSelected || isFailuresLensSelected
    const backgroundSlices =
      useNoisyAmbientBackdrop || activeSlices.length === 0
        ? IDLE_BACKGROUND_SLICES
        : activeSlices
    const backgroundPreset = useNoisyAmbientBackdrop
      ? IDLE_BACKGROUND_PRESET
      : selectedLens.preset
    const fallbackAmbientColor = {
      b: 128,
      g: 128,
      r: 128,
    }
    const resolveAmbientPalette = () => {
      const palette = resolveCanvasColorPalette([AMBIENT_DOT_COLOR])
      return palette.length > 0 ? palette : [fallbackAmbientColor]
    }
    let ambientPalette = resolveAmbientPalette()
    let lensTransitionStart = shouldAnimateLensTransition ? motionClock() : null
    const syncCanvasResolution = () => {
      const nextWidth = Math.max(1, Math.floor(plate.clientWidth))
      const nextHeight = Math.max(1, Math.floor(plate.clientHeight))
      const nextDpr = Math.max(1, window.devicePixelRatio || 1)
      const needsResize =
        width !== nextWidth || height !== nextHeight || dpr !== nextDpr

      if (!needsResize) {
        return
      }

      width = nextWidth
      height = nextHeight
      dpr = nextDpr
      canvas.width = Math.round(width * dpr)
      canvas.height = Math.round(height * dpr)
      context.setTransform(dpr, 0, 0, dpr, 0, 0)

      const particleTarget = Math.max(520, Math.floor((width * height) / 48))
      particles = buildBackgroundParticles(
        particleTarget,
        backgroundSlices,
        backgroundPreset,
        shouldAnimateLensTransition ? previousParticlesRef.current : null,
        transitionSeed,
      )
    }

    const drawAmbientLayer = (timestamp: number) => {
      syncCanvasResolution()
      if (particles.length === 0) {
        return
      }

      const time = timestamp * 0.0012
      context.clearRect(0, 0, width, height)

      const transitionProgress =
        lensTransitionStart === null
          ? 1
          : clampNumber(
              (timestamp - lensTransitionStart) / LENS_TRANSITION_DURATION_MS,
              0,
              1,
            )
      if (transitionProgress >= 1) {
        lensTransitionStart = null
      }

      for (const particle of particles) {
        const chaosPoint = computeBackgroundChaosPoint(particle, time)
        const scatterPoint = computeBackgroundScatterPoint(particle, time)
        const isTransitioning =
          lensTransitionStart !== null && transitionProgress < 1

        let driftPoint
        let animatedPoint: ReturnType<typeof clampToPlate> | null = null
        let nodalStrength = particle.nodalStrength
        let releaseMix = 0
        let regroupMix = 0

        if (useNoisyAmbientBackdrop && !isTransitioning) {
          animatedPoint = computeIdleAmbientParticlePoint(particle, time)
          nodalStrength = IDLE_AMBIENT_NODAL_STRENGTH
        } else if (
          isTransitioning &&
          transitionProgress < LENS_RELEASE_PHASE_SHARE
        ) {
          releaseMix = easeOutCubic(
            clampNumber(transitionProgress / LENS_RELEASE_PHASE_SHARE, 0, 1),
          )
          driftPoint = clampToPlate(
            particle.fromAnchorU +
              (scatterPoint.u - particle.fromAnchorU) * releaseMix,
            particle.fromAnchorV +
              (scatterPoint.v - particle.fromAnchorV) * releaseMix,
          )
          nodalStrength = particle.fromNodalStrength
        } else if (isTransitioning) {
          regroupMix = easeInOutSine(
            clampNumber(
              (transitionProgress - LENS_RELEASE_PHASE_SHARE) /
                (1 - LENS_RELEASE_PHASE_SHARE),
              0,
              1,
            ),
          )
          const regroupTarget = useNoisyAmbientBackdrop
            ? computeIdleAmbientDriftPoint(particle, time)
            : clampToPlate(particle.anchorU, particle.anchorV)
          const regroupTargetMix = useNoisyAmbientBackdrop
            ? clampNumber(
                regroupMix / IDLE_TRANSITION_REGROUP_LANDING_MIX,
                0,
                1,
              )
            : regroupMix
          driftPoint = clampToPlate(
            scatterPoint.u +
              (regroupTarget.u - scatterPoint.u) * regroupTargetMix,
            scatterPoint.v +
              (regroupTarget.v - scatterPoint.v) * regroupTargetMix,
          )
          const toNodalStrength = useNoisyAmbientBackdrop
            ? IDLE_AMBIENT_NODAL_STRENGTH
            : particle.nodalStrength
          nodalStrength =
            particle.fromNodalStrength +
            (toNodalStrength - particle.fromNodalStrength) * regroupTargetMix
        } else {
          const settleMix =
            0.82 + Math.sin(time * 0.32 + particle.phaseB) * 0.06
          const driftScale = 1 - settleMix
          driftPoint = clampToPlate(
            particle.anchorU + (chaosPoint.u - particle.anchorU) * driftScale,
            particle.anchorV + (chaosPoint.v - particle.anchorV) * driftScale,
          )
        }

        const localJitter = 0.0009 + particle.amp * 0.0012
        const transitionTurbulence = isTransitioning
          ? (0.0028 + particle.amp * 0.0034) *
            Math.sin(transitionProgress * Math.PI)
          : 0
        if (!animatedPoint) {
          const resolvedDriftPoint =
            driftPoint ?? clampToPlate(particle.anchorU, particle.anchorV)
          animatedPoint = clampToPlate(
            resolvedDriftPoint.u +
              Math.cos(time * (2.45 + particle.frequencyA) + particle.phaseA) *
                localJitter +
              Math.sin(time * (5.05 + particle.frequencyB) + particle.phaseB) *
                transitionTurbulence,
            resolvedDriftPoint.v +
              Math.sin(time * (2.2 + particle.frequencyB) + particle.phaseB) *
                localJitter +
              Math.cos(time * (4.7 + particle.frequencyA) + particle.phaseA) *
                transitionTurbulence,
          )
        }

        const color =
          ambientPalette[particle.sliceIndex % ambientPalette.length] ??
          ambientPalette[0]
        const edgeAlphaFalloff = clampNumber(
          (1 -
            Math.hypot(animatedPoint.u - 0.5, animatedPoint.v - 0.5) /
              PLATE_DOMAIN_RADIUS) /
            0.2,
          0,
          1,
        )
        const alpha = clampNumber(
          (0.26 + nodalStrength * 0.3) *
            AMBIENT_DOT_ALPHA_SCALE *
            edgeAlphaFalloff,
          0,
          0.75,
        )
        const radius = clampNumber(
          (1.04 + nodalStrength * 0.56) * AMBIENT_DOT_RADIUS_SCALE,
          0.56,
          1.19,
        )
        const projected = projectToScreen(
          animatedPoint.u,
          animatedPoint.v,
          width,
          height,
        )

        context.beginPath()
        context.arc(projected.x, projected.y, radius, 0, Math.PI * 2)
        context.fillStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha.toFixed(3)})`
        context.fill()
      }
    }

    const frame = () => {
      drawAmbientLayer(motionClock())
      animationFrame = window.requestAnimationFrame(frame)
    }

    syncCanvasResolution()
    drawAmbientLayer(motionClock())

    if (!isStillRef.current) {
      animationFrame = window.requestAnimationFrame(frame)
    }

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            syncCanvasResolution()
            drawAmbientLayer(motionClock())
          })
    resizeObserver?.observe(plate)

    const themeObserver = new MutationObserver(() => {
      ambientPalette = resolveAmbientPalette()
      drawAmbientLayer(motionClock())
    })
    themeObserver.observe(document.documentElement, {
      attributeFilter: ["class", "data-theme"],
      attributes: true,
    })

    syncMotionRef.current = () => {
      window.cancelAnimationFrame(animationFrame)
      drawAmbientLayer(motionClock())
      if (!isStillRef.current) {
        animationFrame = window.requestAnimationFrame(frame)
      }
    }

    return () => {
      syncMotionRef.current = null
      previousVisualizationKeyRef.current = selectedVisualizationKey
      previousParticlesRef.current = particles
      themeObserver.disconnect()
      resizeObserver?.disconnect()
      window.cancelAnimationFrame(animationFrame)
    }
  }, [
    activeSlices,
    isFailuresLensSelected,
    isSeedClusterLensSelected,
    motionClock,
    selectedLens,
    selectedVisualizationKey,
  ])

  useEffect(() => {
    isStillRef.current = isStill
    syncMotionRef.current?.()
  }, [isStill])

  const stageDescription = describeVisualizerLens(selectedLens)

  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={setIsExpanded}
      className="@container/visualizer flex-none border-b border-coval-border bg-surface-contrast px-6 py-2"
      data-testid="uploaded-conversations-visualizer-panel"
    >
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <UploadedConversationsVisualizerRunStatus
            conversationCount={displayedConversationCount}
            errorStatus={latestRunQuery.error?.status}
            isEmptyRun={hasCompletedEmptyRun}
            isPending={latestRunQuery.isPending}
            isReadFailure={isLatestRunReadFailure}
            onRetry={() => void latestRunQuery.refetch()}
            showFallback={showFallback}
          />
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              aria-label={isExpanded ? "Collapse signals" : "Expand signals"}
            >
              {isExpanded ? "Collapse" : "Expand"}
              {isExpanded ? (
                <ChevronUp aria-hidden="true" />
              ) : (
                <ChevronDown aria-hidden="true" />
              )}
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent
          forceMount
          hidden={!isExpanded}
          data-testid="uploaded-conversations-visualizer-body"
        >
          {!hasCompletedEmptyRun ? (
            <div className="grid max-h-[45dvh] items-center gap-3 overflow-y-auto @min-[580px]/visualizer:max-h-none @min-[580px]/visualizer:grid-cols-[minmax(130px,0.7fr)_minmax(220px,320px)_minmax(190px,1.3fr)] @min-[580px]/visualizer:overflow-visible">
              <UploadedConversationsVisualizerControlsColumn
                agents={agents}
                hasMultipleAgents={hasMultipleAgents}
                onSelectAgent={selectAgent}
                onSelectLens={selectLens}
                selectedAgentKey={selectedAgent.key}
                selectedLensKey={selectedLensKey}
                selectedLenses={selectedAgent.lenses}
              />

              <div className="relative mx-auto aspect-square w-full max-w-[320px] shrink-0">
                <div
                  className="absolute inset-0"
                  data-testid="uploaded-conversations-visualizer-stage"
                  role="img"
                  aria-label={stageDescription.label}
                  aria-describedby={stageSummaryId}
                >
                  <p id={stageSummaryId} className="sr-only">
                    {stageDescription.summary}
                  </p>
                  <div className="absolute inset-[6%]">
                    <div className="pointer-events-none absolute inset-0 rounded-full bg-surface-contrast" />
                    <canvas
                      ref={underlayCanvasRef}
                      aria-hidden="true"
                      className={cn(
                        "pointer-events-none absolute inset-0 h-full w-full",
                        UNDERLAY_MASK_CLASS,
                      )}
                      data-testid="uploaded-conversations-conversation-underlays"
                    />

                    <div
                      ref={plateRef}
                      className="absolute inset-0 overflow-hidden rounded-full"
                    >
                      <canvas
                        ref={canvasRef}
                        aria-hidden="true"
                        className={cn(
                          "absolute inset-0 h-full w-full",
                          PLATE_MASK_CLASS,
                        )}
                      />
                      <UploadedConversationsVisualizerConversationOverlay
                        activeBucketKey={activeLegendBucketKey}
                        activeAnomalyMarkerColor={
                          isAnomaliesLensSelected
                            ? activeAnomalyMarkerColor
                            : null
                        }
                        className={PLATE_MASK_CLASS}
                        highlightedConversationIds={
                          activeAnomalyConversationIds
                        }
                        isStill={isStill}
                        lens={selectedLens}
                        onConversationClick={handleConversationClick}
                        onSliceHoverChange={handleSliceHoverChange}
                        renderUnderlay={
                          !isAnomaliesLensSelected &&
                          selectedLens.key !== NO_LENS_VISUALIZER_LENS_KEY
                        }
                        sliceFillPalette={sliceFillPalette}
                        sliceMarkPalette={sliceMarkPalette}
                        slices={activeSlices}
                        underlayCanvasRef={underlayCanvasRef}
                        visualizationKey={selectedVisualizationKey}
                      />
                    </div>
                  </div>
                </div>
                <MotionPauseButton
                  isStill={isStill}
                  onToggle={toggleStill}
                  className="absolute right-0 bottom-0"
                />
              </div>

              <div className="relative h-56 min-h-0 min-w-0 self-stretch @min-[580px]/visualizer:h-[320px]">
                <div className="absolute inset-0 min-h-0 min-w-0 overflow-hidden">
                  {isFailuresLensSelected && selectedLens.failureOutput ? (
                    <UploadedConversationsVisualizerFailureGroups
                      lens={selectedLens.failureOutput}
                      onFilterSimulationOutputs={handleConversationIdsFilter}
                      onHighlightIssues={setActiveAnomalyConversationIds}
                      seedKey={`${selectedAgent.key}:${latestRunQuery.data?.run_id}`}
                    />
                  ) : isAnomaliesLensSelected ? (
                    <UploadedConversationsVisualizerAnomaliesQuestions
                      bucketAssignments={selectedLens.bucketAssignments}
                      buckets={selectedLens.buckets}
                      markerPalette={sliceMarkPalette}
                      onActiveMarkerColorChange={setActiveAnomalyMarkerColor}
                      onHighlightedConversationIdsChange={
                        setActiveAnomalyConversationIds
                      }
                      seedKey={selectedAgent.key}
                    />
                  ) : isNoLensSelected ? (
                    <div className="flex h-full min-h-0 min-w-0 self-stretch p-1">
                      <div className="flex h-full w-full items-center px-0.5">
                        <p
                          className="text-sm leading-5 text-coval-text-muted"
                          data-testid="uploaded-conversations-no-lens-static-legend"
                        >
                          {selectedLens.title}
                        </p>
                      </div>
                    </div>
                  ) : selectedLens.callTopicParents ? (
                    <UploadedConversationsCallTopicTree
                      key={selectedVisualizationKey}
                      onBucketClick={handleLegendBucketClick}
                      onBucketHoverChange={handleLegendBucketHoverChange}
                      onParentClick={handleConversationIdsFilter}
                      parents={selectedLens.callTopicParents}
                    />
                  ) : (
                    <UploadedConversationsVisualizerLegendReel
                      activeBucketKey={activeLegendBucketKey}
                      onBucketClick={handleLegendBucketClick}
                      onBucketHoverChange={handleLegendBucketHoverChange}
                      sliceMarkPalette={sliceMarkPalette}
                      slices={activeSlices}
                    />
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}
